import { useRef, useEffect, useState } from "react";

/* Module-level scroll direction tracker */
let _scrollDir = "down";
let _lastScrollY = typeof window !== "undefined" ? window.scrollY : 0;

if (typeof window !== "undefined") {
  window.addEventListener("scroll", () => {
    const curr = window.scrollY;
    _scrollDir = curr >= _lastScrollY ? "down" : "up";
    _lastScrollY = curr;
  }, { passive: true });
}

export function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [dir, setDir] = useState("down");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setDir(_scrollDir);
        setInView(true);
      } else {
        setInView(false);
      }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  const ease = "cubic-bezier(.2,.6,.2,1)";
  const mob = typeof window !== "undefined" && window.innerWidth < 840;

  const fromDir = (delay = 0) => {
    const hidden = mob
      ? (dir === "up" ? "translate(-40px, -28px)" : "translate(40px, 28px)")
      : (dir === "up" ? "translateY(-52px)" : "translateY(52px)");
    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translate(0, 0)" : hidden,
      transition: `opacity 0.9s ${ease} ${delay}ms, transform 1s ${ease} ${delay}ms`,
    };
  };

  const fromLeft = (delay = 0) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translate(0, 0)" : mob ? "translate(-48px, 24px)" : "translateX(-64px)",
    transition: `opacity 0.9s ${ease} ${delay}ms, transform 1s ${ease} ${delay}ms`,
  });

  const fromRight = (delay = 0) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? "translate(0, 0)" : mob ? "translate(48px, 24px)" : "translateX(64px)",
    transition: `opacity 0.9s ${ease} ${delay}ms, transform 1s ${ease} ${delay}ms`,
  });

  /* Diagonal: down → from bottom-right, up → from top-left */
  const fromDiagonal = (delay = 0, dist = 64) => ({
    opacity: inView ? 1 : 0,
    transform: inView
      ? "translate(0, 0)"
      : dir === "up"
        ? `translate(-${dist}px, -${dist * 0.6}px)`
        : `translate(${dist}px, ${dist * 0.6}px)`,
    transition: `opacity 1s ${ease} ${delay}ms, transform 1.1s ${ease} ${delay}ms`,
  });

  return { ref, inView, dir, fromDir, fromLeft, fromRight, fromDiagonal };
}
